import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Spinner, FileXls, Warning, Trash, ArrowCounterClockwise, DownloadSimple, LinkSimple, Package, X,
} from '@phosphor-icons/react';
import { downloadWithAuth } from '@/lib/apiClient';
import { useNarrow, MobileRow } from '@/components/historico/MobileRows';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const brl = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const brlC = (c) => brl((c || 0) / 100);
const fmtDate = (iso) => {
  if (!iso) return '(sem data)';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
};
const fmtDT = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }); } catch { return iso; }
};
const ano = new Date().getFullYear();
const errMsg = (d, fallback) => (typeof d?.detail === 'string' ? d.detail : d?.detail?.message) || fallback;

const Selo = ({ children, tone = 'zinc', testId }) => {
  const tones = {
    zinc: 'bg-[#F4F4F5] text-[#71717A]', amber: 'bg-[#FEF3C7] text-[#92400E]',
    blue: 'bg-[#DBEAFE] text-[#1E40AF]', red: 'bg-[#FEE2E2] text-[#991B1B]', green: 'bg-[#DCFCE7] text-[#166534]',
  };
  return <span data-testid={testId} className={`ml-1 whitespace-nowrap text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide ${tones[tone]}`}>{children}</span>;
};

const Kpi = ({ label, value, testId }) => (
  <div className="rounded-xl border border-[#E4E4E7] p-3 bg-white" data-testid={testId}>
    <div className="text-[10px] uppercase text-[#71717A] tracking-wide">{label}</div>
    <div className="text-lg font-semibold text-[#18181B] whitespace-nowrap">{value}</div>
  </div>
);

// ---------- selecao: registros ligados andam juntos ----------
const emptySel = () => ({ dias: new Set(), movs: new Set(), cmds: new Set() });

const closure = (dia, sel, add, { movIds = [], cmdIds = [], diaCaixa = false }) => {
  const movs = new Set(sel.movs); const cmds = new Set(sel.cmds); const dias = new Set(sel.dias);
  const movById = Object.fromEntries(dia.movimentacoes.map(m => [m.id, m]));
  const cmdById = Object.fromEntries(dia.comandas.map(c => [c.id, c]));
  const qm = [...movIds]; const qc = [...cmdIds]; const seenM = new Set(); const seenC = new Set();
  while (qm.length || qc.length) {
    if (qm.length) {
      const id = qm.pop(); if (seenM.has(id)) continue; seenM.add(id);
      const lig = movById[id]?.comanda_ligada?.id; if (lig) qc.push(lig);
    } else {
      const id = qc.pop(); if (seenC.has(id)) continue; seenC.add(id);
      (cmdById[id]?.movs_ligadas || []).forEach(m => qm.push(m));
    }
  }
  seenM.forEach(id => (add ? movs.add(id) : movs.delete(id)));
  seenC.forEach(id => (add ? cmds.add(id) : cmds.delete(id)));
  if (diaCaixa && add) dias.add(dia.data);
  if (!add && dia.caixa && (diaCaixa || seenM.size)) dias.delete(dia.data);
  return { dias, movs, cmds };
};

const DiaHeader = ({ dia, checked, onToggle }) => {
  const c = dia.caixa;
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 sm:px-4 py-3 bg-[#F7F6F4] border-b border-[#E4E4E7]">
      <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`sel-dia-${dia.data || 'sem-data'}`} className="w-5 h-5 sm:w-4 sm:h-4 accent-[#1B4332]" />
      <span className="font-semibold text-[#18181B]">{fmtDate(dia.data)}</span>
      {c ? (
        <span className="text-xs text-[#52525B] min-w-0 break-words">
          Caixa <b>{c.status}</b> · saldo inicial {brl(c.saldo_inicial)} · aberto {fmtDT(c.opened_at)} {c.responsavel ? `por ${c.responsavel}` : ''}
          {c.closed_at ? ` · fechado ${fmtDT(c.closed_at)}` : ''} · {c.n_movimentacoes} mov. · entradas {brl(c.total_entradas)} · saídas {brl(c.total_saidas)}
          {c.sem_movimento && <Selo testId={`selo-sem-mov-${dia.data || 'sem-data'}`}>sem movimento</Selo>}
          {c.retroativo_flag && <Selo tone="amber">retroativo</Selo>}
          {c.status === 'fechado' && <Selo tone="blue">fechado</Selo>}
        </span>
      ) : <span className="text-xs text-[#A1A1AA]">sem caixa aberto neste dia</span>}
      <span className={`sm:ml-auto text-xs ${dia.tem_historico ? 'text-[#6B21A8] font-medium' : 'text-[#A1A1AA]'}`} data-testid={`hist-dia-${dia.data || 'sem-data'}`}>
        {dia.tem_historico ? `Histórico Avec neste dia: ${brlC(dia.historico_total_centavos)}` : 'Sem histórico Avec neste dia'}
      </span>
    </div>
  );
};

const Check = ({ checked, onChange, testId }) => (
  <input type="checkbox" checked={checked} onChange={onChange} onClick={e => e.stopPropagation()} data-testid={testId} className="w-5 h-5 mr-2 align-middle accent-[#1B4332]" />
);

const MovsCards = ({ dia, sel, onToggle }) => (
  <div className="space-y-2">
    {dia.movimentacoes.map(m => (
      <MobileRow key={m.id} testId={`mov-row-${m.id}`} active={sel.movs.has(m.id)}
        title={<><Check checked={sel.movs.has(m.id)} onChange={() => onToggle(m.id)} testId={`sel-mov-${m.id}`} />{m.tipo}{m.retroativo_flag && <Selo tone="amber">retroativo</Selo>}</>}
        value={<span className={['entrada', 'suprimento'].includes(m.tipo) ? 'text-[#166534]' : 'text-[#B91C1C]'}>{brl(m.valor)}</span>}
        meta={[['', m.forma_pagamento], ['Origem', m.origem], ['', m.descricao], ['Criada', fmtDT(m.created_at)],
          ['Comanda', m.comanda_ligada ? <span key="c">{m.comanda_ligada.codigo} <Selo>deduzida pelo código</Selo></span> : '']]} />
    ))}
  </div>
);

const MovsTable = (props) => (useNarrow() ? <MovsCards {...props} /> : <MovsTableWide {...props} />);

const MovsTableWide = ({ dia, sel, onToggle }) => (
  <table className="w-full text-xs">
    <thead className="text-[#A1A1AA] text-[10px] uppercase"><tr>
      <th className="w-8" /><th className="text-left py-1">Tipo</th><th className="text-right">Valor</th><th className="text-left pl-3">Forma</th>
      <th className="text-left">Origem</th><th className="text-left">Descrição</th><th className="text-left">Criada em</th><th className="text-left">Comanda ligada</th>
    </tr></thead>
    <tbody>{dia.movimentacoes.map(m => (
      <tr key={m.id} className="border-t border-[#F4F4F5]" data-testid={`mov-row-${m.id}`}>
        <td className="py-1.5 pl-2"><input type="checkbox" checked={sel.movs.has(m.id)} onChange={() => onToggle(m.id)} data-testid={`sel-mov-${m.id}`} className="accent-[#1B4332]" /></td>
        <td>{m.tipo}{m.retroativo_flag && <Selo tone="amber">retroativo</Selo>}</td>
        <td className={`text-right ${['entrada', 'suprimento'].includes(m.tipo) ? 'text-[#166534]' : 'text-[#B91C1C]'}`}>{brl(m.valor)}</td>
        <td className="pl-3">{m.forma_pagamento}</td><td>{m.origem}</td><td className="max-w-[280px] truncate" title={m.descricao}>{m.descricao}</td>
        <td>{fmtDT(m.created_at)}</td>
        <td>{m.comanda_ligada ? <span title="Ligação deduzida pelo código CMD no início da descrição (não há id gravado)"><LinkSimple size={12} className="inline" /> {m.comanda_ligada.codigo} <Selo>deduzida pelo código</Selo></span> : ''}</td>
      </tr>
    ))}</tbody>
  </table>
);

const EstoqueInfo = ({ estoque }) => (
  <div className="text-[11px] text-[#92400E]">
    {estoque.map(e => (
      <div key={e.mov_estoque_id}><Package size={11} className="inline" /> baixou {e.quantidade} un. de <b>{e.produto_nome}</b>; ao excluir, volta +{e.quantidade}
        {!e.produto_existe && <Selo tone="red">produto não existe mais</Selo>}</div>
    ))}
  </div>
);

const CmdsCards = ({ dia, sel, onToggle }) => (
  <div className="space-y-2">
    {dia.comandas.map(c => (
      <MobileRow key={c.id} testId={`cmd-row-${c.codigo}`} active={sel.cmds.has(c.id)}
        title={<><Check checked={sel.cmds.has(c.id)} onChange={() => onToggle(c.id)} testId={`sel-cmd-${c.codigo}`} />{c.codigo}{c.retroativo_flag && <Selo tone="amber">retroativo</Selo>}</>}
        value={brl(c.total)}
        meta={[['', c.cliente_nome], ['', c.profissional_nome], ['Itens', c.n_itens], ['', c.status], ['Criada', fmtDT(c.created_at)],
          ['Justificativa', c.retroativo_justificativa ? `“${c.retroativo_justificativa}”` : '']]}>
        {c.movs_ligadas.length > 0 && <div className="mt-1 text-[11px] text-[#1E40AF]"><LinkSimple size={11} className="inline" /> {c.movs_ligadas.length} movimentação(ões) de caixa (deduzida pelo código)</div>}
        {c.estoque.length > 0 && <div className="mt-1"><EstoqueInfo estoque={c.estoque} /></div>}
      </MobileRow>
    ))}
  </div>
);

const CmdsTable = (props) => (useNarrow() ? <CmdsCards {...props} /> : <CmdsTableWide {...props} />);

const CmdsTableWide = ({ dia, sel, onToggle }) => (
  <table className="w-full text-xs">
    <thead className="text-[#A1A1AA] text-[10px] uppercase"><tr>
      <th className="w-8" /><th className="text-left py-1">Código</th><th className="text-left">Cliente</th><th className="text-left">Profissional</th>
      <th className="text-right">Itens</th><th className="text-right">Total</th><th className="text-left pl-3">Status</th><th className="text-left">Criada em</th><th className="text-left">Ligações / estoque</th>
    </tr></thead>
    <tbody>{dia.comandas.map(c => (
      <tr key={c.id} className="border-t border-[#F4F4F5] align-top" data-testid={`cmd-row-${c.codigo}`}>
        <td className="py-1.5 pl-2"><input type="checkbox" checked={sel.cmds.has(c.id)} onChange={() => onToggle(c.id)} data-testid={`sel-cmd-${c.codigo}`} className="accent-[#1B4332]" /></td>
        <td className="font-medium">{c.codigo}{c.retroativo_flag && <Selo tone="amber">retroativo</Selo>}
          {c.retroativo_justificativa && <div className="text-[10px] text-[#71717A]">“{c.retroativo_justificativa}”</div>}</td>
        <td>{c.cliente_nome}</td><td>{c.profissional_nome}</td><td className="text-right">{c.n_itens}</td>
        <td className="text-right">{brl(c.total)}</td><td className="pl-3">{c.status}</td><td>{fmtDT(c.created_at)}</td>
        <td>
          {c.movs_ligadas.length > 0 && <div className="text-[11px] text-[#1E40AF]"><LinkSimple size={11} className="inline" /> {c.movs_ligadas.length} movimentação(ões) de caixa (deduzida pelo código)</div>}
          {c.estoque.length > 0 && <EstoqueInfo estoque={c.estoque} />}
        </td>
      </tr>
    ))}</tbody>
  </table>
);

const DiaCard = ({ dia, sel, setSel }) => {
  const allMovs = dia.movimentacoes.map(m => m.id); const allCmds = dia.comandas.map(c => c.id);
  const checked = (!dia.caixa || sel.dias.has(dia.data)) && allMovs.every(id => sel.movs.has(id)) && allCmds.every(id => sel.cmds.has(id));
  const toggleDia = () => setSel(s => closure(dia, s, !checked, { movIds: allMovs, cmdIds: allCmds, diaCaixa: !!dia.caixa }));
  const toggleMov = (id) => setSel(s => closure(dia, s, !s.movs.has(id), { movIds: [id] }));
  const toggleCmd = (id) => setSel(s => closure(dia, s, !s.cmds.has(id), { cmdIds: [id] }));
  return (
    <div className="bg-white rounded-xl border border-[#E4E4E7] overflow-hidden mb-3" data-testid={`dia-card-${dia.data || 'sem-data'}`}>
      <DiaHeader dia={dia} checked={checked} onToggle={toggleDia} />
      {dia.movimentacoes.length > 0 && <div className="px-2 py-2"><div className="text-[10px] uppercase text-[#71717A] px-2 mb-1">Movimentações de caixa</div><MovsTable dia={dia} sel={sel} onToggle={toggleMov} /></div>}
      {dia.comandas.length > 0 && <div className="px-2 py-2 border-t border-[#F4F4F5]"><div className="text-[10px] uppercase text-[#71717A] px-2 mb-1">Comandas</div><CmdsTable dia={dia} sel={sel} onToggle={toggleCmd} /></div>}
    </div>
  );
};

// ---------- autorizacao (mesma usada nas acoes retroativas) ----------
const AuthFields = ({ auth, setAuth }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
    <input placeholder="Login ADMIN" value={auth.admin_login} onChange={e => setAuth({ ...auth, admin_login: e.target.value })} data-testid="limpeza-admin-login" className="px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" />
    <input placeholder="Senha ADMIN" type="password" value={auth.admin_password} onChange={e => setAuth({ ...auth, admin_password: e.target.value })} data-testid="limpeza-admin-password" className="px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" />
    <input placeholder="ou senha master" type="password" value={auth.senha_master} onChange={e => setAuth({ ...auth, senha_master: e.target.value })} data-testid="limpeza-senha-master" className="px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" />
  </div>
);
const emptyAuth = { admin_login: '', admin_password: '', senha_master: '' };
const authOk = (a) => (a.admin_login && a.admin_password) || a.senha_master;

const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid={testId}>
    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
      <div className="flex items-center mb-4"><h3 className="text-lg font-semibold text-[#18181B]">{title}</h3>
        <button onClick={onClose} className="ml-auto text-[#71717A]" data-testid="modal-fechar"><X size={18} /></button></div>
      {children}
    </div>
  </div>
);

const PlanoResumo = ({ plano }) => {
  const r = plano.resumo;
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi label="Dias de caixa" value={r.dias} testId="conf-dias" /><Kpi label="Movimentações" value={r.movimentacoes} testId="conf-movs" />
        <Kpi label="Comandas" value={r.comandas} testId="conf-cmds" /><Kpi label="Saídas de estoque" value={r.saidas_estoque} testId="conf-estq" />
      </div>
      <div className="text-xs text-[#52525B]" data-testid="conf-somas">Entradas {brl(r.entradas)} · Saídas {brl(r.saidas)} · Total das comandas {brl(r.total_comandas)}</div>
      {r.dias_fechados > 0 && <div className="p-2 rounded-lg bg-[#DBEAFE] text-[#1E40AF] text-xs" data-testid="conf-aviso-fechado"><Warning size={12} className="inline" /> {r.dias_fechados} dia(s) selecionado(s) eram caixa FECHADO.</div>}
      {plano.auto_incluidos.length > 0 && (
        <div className="p-2 rounded-lg bg-[#FEF3C7] text-[#92400E] text-xs" data-testid="conf-auto">
          <b>Incluídos automaticamente (registros ligados andam juntos):</b>
          <ul className="list-disc ml-4">{plano.auto_incluidos.map((a, i) => <li key={i}>{a.tipo} — {a.motivo}</li>)}</ul>
        </div>
      )}
      {plano.estoque_efeito.length > 0 && (
        <div className="p-2 rounded-lg bg-[#FFF7ED] border border-[#FED7AA] text-[#9A3412] text-xs" data-testid="conf-estoque">
          <b>Efeito no estoque (PAC MAN):</b>
          <ul className="list-disc ml-4">{plano.estoque_efeito.map(e => <li key={e.produto_id}>{e.comandas.join(', ')}: baixou {e.quantidade_devolvida} un. de <b>{e.produto_nome}</b>; ao excluir, o estoque de {e.produto_nome} volta +{e.quantidade_devolvida}</li>)}</ul>
        </div>
      )}
      {plano.bloqueios.length > 0 && (
        <div className="p-2 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-xs" data-testid="conf-bloqueios">
          <b>Bloqueado — remova da seleção:</b>
          <ul className="list-disc ml-4">{plano.bloqueios.map((b, i) => <li key={i}>{b.tipo} {b.codigo}: {b.motivo}</li>)}</ul>
        </div>
      )}
    </div>
  );
};

const ConfirmarLimpeza = ({ selecao, onClose, onDone }) => {
  const [plano, setPlano] = useState(null); const [erro, setErro] = useState('');
  const [just, setJust] = useState(''); const [ciente, setCiente] = useState(false);
  const [auth, setAuth] = useState(emptyAuth); const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/lancamentos-manuais/limpeza/preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(selecao) });
      const d = await r.json(); if (r.ok) setPlano(d); else setErro(errMsg(d, 'Falha na prévia'));
    })();
  }, [selecao]);
  const precisaCiente = plano?.estoque_efeito?.length > 0;
  const pode = plano?.pode_excluir && just.trim().length >= 10 && authOk(auth) && (!precisaCiente || ciente) && !busy;
  const executar = async () => {
    setBusy(true); setErro('');
    const r = await fetch(`${API}/lancamentos-manuais/limpeza`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selecao, justificativa: just, ciente_estoque: ciente, ...auth }) });
    const d = await r.json(); setBusy(false);
    if (r.ok) onDone(d); else setErro(errMsg(d, 'Falha ao excluir'));
  };
  return (
    <Modal title="Confirmar exclusão de lançamentos manuais" onClose={onClose} testId="modal-confirmar-limpeza">
      {!plano && !erro && <Spinner className="animate-spin" />}
      {plano && <PlanoResumo plano={plano} />}
      {plano?.pode_excluir && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-[#52525B]">Uma cópia de segurança completa é gravada antes de apagar e pode ser baixada ou restaurada depois. Os códigos CMD apagados não são reutilizados.</p>
          {precisaCiente && <label className="flex items-center gap-2 text-sm text-[#9A3412]"><input type="checkbox" checked={ciente} onChange={e => setCiente(e.target.checked)} data-testid="limpeza-ciente-estoque" className="accent-[#9A3412]" /> Estou ciente de que o estoque será devolvido</label>}
          <textarea placeholder="Justificativa (obrigatória, mínimo 10 caracteres)" value={just} onChange={e => setJust(e.target.value)} data-testid="limpeza-justificativa" className="w-full px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" rows={2} />
          <AuthFields auth={auth} setAuth={setAuth} />
        </div>
      )}
      {erro && <div className="mt-3 p-2 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="limpeza-erro">{erro}</div>}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button onClick={onClose} className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-4 py-2 rounded-xl border border-[#E4E4E7] text-sm" data-testid="limpeza-cancelar">Cancelar</button>
        <button onClick={executar} disabled={!pode} data-testid="limpeza-confirmar" className="flex-1 sm:flex-none justify-center min-h-[44px] sm:min-h-0 px-4 py-2 rounded-xl text-sm text-white bg-[#B91C1C] disabled:opacity-40 inline-flex items-center gap-1">
          {busy ? <Spinner className="animate-spin" size={14} /> : <Trash size={14} />} Excluir definitivamente
        </button>
      </div>
    </Modal>
  );
};

const RestaurarModal = ({ limpeza, onClose, onDone }) => {
  const [just, setJust] = useState(''); const [auth, setAuth] = useState(emptyAuth); const [erro, setErro] = useState(''); const [busy, setBusy] = useState(false);
  const restaurar = async () => {
    setBusy(true); setErro('');
    const r = await fetch(`${API}/lancamentos-manuais/limpeza/${limpeza.id}/restaurar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ justificativa: just, ...auth }) });
    const d = await r.json(); setBusy(false);
    if (r.ok) onDone(); else setErro(errMsg(d, 'Falha ao restaurar') + (d?.detail?.conflitos ? ` (${d.detail.conflitos.length} conflito(s))` : ''));
  };
  return (
    <Modal title="Restaurar limpeza" onClose={onClose} testId="modal-restaurar">
      <p className="text-sm text-[#52525B] mb-3">Devolve exatamente os registros apagados (mesmos ids){limpeza.estoque_efeito?.length ? ' e reaplica as saídas de estoque' : ''}. É recusado se houver conflito.</p>
      <textarea placeholder="Justificativa (mínimo 10 caracteres)" value={just} onChange={e => setJust(e.target.value)} data-testid="restaurar-justificativa" className="w-full px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm mb-3" rows={2} />
      <AuthFields auth={auth} setAuth={setAuth} />
      {erro && <div className="mt-3 p-2 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="restaurar-erro">{erro}</div>}
      <div className="mt-4 flex justify-end">
        <button onClick={restaurar} disabled={busy || just.trim().length < 10 || !authOk(auth)} data-testid="restaurar-confirmar" className="px-4 py-2 rounded-xl text-sm text-white bg-[#1B4332] disabled:opacity-40">Restaurar</button>
      </div>
    </Modal>
  );
};

const LimpezasList = ({ limpezas, onRestaurar }) => (
  <div className="bg-white rounded-xl border border-[#E4E4E7] p-4 mt-6" data-testid="limpezas-list">
    <div className="text-xs font-semibold text-[#71717A] uppercase mb-2">Limpezas realizadas (cópias de segurança)</div>
    {!limpezas.length && <div className="text-xs text-[#A1A1AA]">Nenhuma limpeza realizada.</div>}
    {limpezas.map(l => (
      <div key={l.id} className="flex flex-wrap items-center gap-3 py-2 border-t border-[#F4F4F5] text-xs min-w-0 break-words" data-testid={`limpeza-${l.id}`}>
        <span>{fmtDT(l.criado_em)}</span><span>por {l.usuario?.nome}</span>
        <span>{l.resumo.dias} dia(s) · {l.resumo.movimentacoes} mov. · {l.resumo.comandas} comanda(s) · {l.resumo.saidas_estoque} saída(s) de estoque</span>
        <span className="text-[#71717A] italic max-w-[260px] truncate" title={l.justificativa}>“{l.justificativa}”</span>
        <Selo tone={l.status === 'aplicada' ? 'red' : 'green'}>{l.status}</Selo>
        <div className="ml-auto flex gap-2">
          <button onClick={() => downloadWithAuth(`${API}/lancamentos-manuais/limpeza/${l.id}/download`, `limpeza_${l.id.slice(0, 8)}.json`)} data-testid={`limpeza-download-${l.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#E4E4E7]"><DownloadSimple size={12} /> Cópia</button>
          {l.status === 'aplicada' && <button onClick={() => onRestaurar(l)} data-testid={`limpeza-restaurar-${l.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#1B4332] text-[#1B4332]"><ArrowCounterClockwise size={12} /> Restaurar limpeza</button>}
        </div>
      </div>
    ))}
  </div>
);

export const LancamentosManuais = () => {
  const [inicio, setInicio] = useState(`${ano}-01-01`); const [fim, setFim] = useState(`${ano}-12-31`);
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false); const [erro, setErro] = useState('');
  const [sel, setSel] = useState(emptySel); const [confirmar, setConfirmar] = useState(false);
  const [limpezas, setLimpezas] = useState([]); const [restaurando, setRestaurando] = useState(null); const [msg, setMsg] = useState('');

  const carregar = useCallback(async () => {
    if (!inicio || !fim) return;
    setLoading(true); setErro('');
    try {
      const [r, l] = await Promise.all([fetch(`${API}/lancamentos-manuais?inicio=${inicio}&fim=${fim}`), fetch(`${API}/lancamentos-manuais/limpezas`)]);
      const d = await r.json(); if (!r.ok) throw new Error(errMsg(d, 'Falha ao carregar'));
      setData(d); setLimpezas((await l.json()).limpezas || []); setSel(emptySel());
    } catch (e) { setErro(e.message); } finally { setLoading(false); }
  }, [inicio, fim]);
  useEffect(() => { carregar(); }, [carregar]);

  const selecao = useMemo(() => ({ dias: [...sel.dias], movimentacoes: [...sel.movs], comandas: [...sel.cmds] }), [sel]);
  const nSel = sel.dias.size + sel.movs.size + sel.cmds.size;
  const t = data?.totais;

  return (
    <div data-testid="lancamentos-manuais">
      <p className="text-sm text-[#52525B] mb-4">Tudo o que foi lançado à mão no SalonUP (caixas, movimentações e comandas), com o histórico Avec do mesmo dia para comparação. Selecione o que deve sair; a exclusão grava uma cópia de segurança e pode ser restaurada.</p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-[#71717A] w-[calc(50%-6px)] sm:w-auto">Início<input type="date" value={inicio} onChange={e => setInicio(e.target.value)} data-testid="lm-inicio" className="block w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" /></label>
        <label className="text-xs text-[#71717A] w-[calc(50%-6px)] sm:w-auto">Fim<input type="date" value={fim} onChange={e => setFim(e.target.value)} data-testid="lm-fim" className="block w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" /></label>
        <button onClick={() => downloadWithAuth(`${API}/lancamentos-manuais/export/excel?inicio=${inicio}&fim=${fim}`, `lancamentos_manuais_${inicio}_a_${fim}.xlsx`)} data-testid="lm-export-excel" className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0 inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-[#1B4332] text-[#1B4332] text-sm"><FileXls size={16} /> Exportar Excel</button>
        <button onClick={() => setConfirmar(true)} disabled={!nSel} data-testid="lm-excluir-selecionados" className="w-full sm:w-auto sm:ml-auto justify-center min-h-[44px] sm:min-h-0 inline-flex items-center gap-1 px-4 py-2 rounded-xl text-sm text-white bg-[#B91C1C] disabled:opacity-40"><Trash size={16} /> Excluir selecionados ({nSel})</button>
      </div>
      {msg && <div className="mb-3 p-3 rounded-xl bg-[#DCFCE7] text-[#166534] text-sm" data-testid="lm-msg">{msg}</div>}
      {erro && <div className="mb-3 p-3 rounded-xl bg-red-50 text-red-700 text-sm" data-testid="lm-erro">{erro}</div>}
      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          <Kpi label="Dias" value={t.dias} testId="lm-kpi-dias" /><Kpi label="Movimentações" value={t.movimentacoes} testId="lm-kpi-movs" />
          <Kpi label="Comandas" value={t.comandas} testId="lm-kpi-cmds" /><Kpi label="Entradas" value={brl(t.entradas)} testId="lm-kpi-entradas" />
          <Kpi label="Saídas" value={brl(t.saidas)} testId="lm-kpi-saidas" />
        </div>
      )}
      {loading && <Spinner className="animate-spin" />}
      {!loading && data?.dias.map(d => <DiaCard key={d.data || 'sem-data'} dia={d} sel={sel} setSel={setSel} />)}
      {!loading && data && !data.dias.length && <div className="text-sm text-[#A1A1AA]">Nenhum lançamento manual no período.</div>}
      <LimpezasList limpezas={limpezas} onRestaurar={setRestaurando} />
      {confirmar && <ConfirmarLimpeza selecao={selecao} onClose={() => setConfirmar(false)} onDone={(d) => { setConfirmar(false); setMsg(`Limpeza concluída: ${d.resumo.dias} dia(s), ${d.resumo.movimentacoes} mov., ${d.resumo.comandas} comanda(s). Cópia de segurança gravada.`); carregar(); }} />}
      {restaurando && <RestaurarModal limpeza={restaurando} onClose={() => setRestaurando(null)} onDone={() => { setRestaurando(null); setMsg('Limpeza restaurada.'); carregar(); }} />}
    </div>
  );
};

export default LancamentosManuais;
